/**
 * How a fourball's four traces are told apart on one hole plan.
 *
 * Three channels per player, never one: a TONE, a DASH PATTERN, and the
 * INITIAL printed inside each strike mark. Colour alone fails for the ~8% of
 * men with a colour vision deficiency, fails again in a screenshot printed on
 * a clubhouse noticeboard, and fails a third time where two traces cross — so
 * the dash is not decoration, it is the channel that survives all three.
 *
 * ## Why these four tones, and why the two that are missing
 *
 * A round seats at most four players (`Round::MAX_PLAYERS`), so this is a
 * table of exactly four rather than a generator. They are the club's own
 * tokens, in descending weight, and each is a TEXT-SAFE colour on the club's
 * paper — which is what lets `TraceLegend` print a player's name in their own
 * tone rather than inventing a second, unrelated label colour.
 *
 *   * **`flag` is excluded** — the pin-flag red is the club's single hot
 *     accent and its budget is one call to action per page
 *     (docs/fireside/golf-design-system.md). A trace wearing it would compete
 *     with the page's own CTA and teach members that red means "player two".
 *   * **plain `brass` is excluded** — 3.38:1 on paper, under AA, and the
 *     design system states it outright: brass is not a text colour in either
 *     direction. `brass-deep` is its text-safe sibling (4.6:1) and is what
 *     sits in the table instead. A hairline RULE may be brass; a thing a
 *     member has to read may not.
 *
 * ## Why names, never hex
 *
 * Every value below is a CSS custom property NAME. The club's palette is
 * authored once in `entrypoints/fireside.css` and deliberately kept in hex so
 * a reviewer can diff a token against its design card; a second copy of
 * `#153a2a` in TypeScript is a palette that drifts silently the day the card
 * is corrected. Components spend these through `traceColor()`, which is the
 * only place `var(...)` is written.
 */

export interface TraceStyle {
  /**
   * The custom property NAME — `--color-pine`, not `#153a2a` and not
   * `var(--color-pine)`. `traceColor()` wraps it.
   */
  colorVariable: string
  /**
   * SVG `stroke-dasharray`, or undefined for the solid first seat. In user
   * units, and paired with `vectorEffect="non-scaling-stroke"` so the pattern
   * keeps its rhythm however the plan is fitted.
   */
  dash?: string
  /** For a `<title>`/`<desc>` that has to say which line is whose. */
  label: string
}

/**
 * Seats 1-4. The dashes lengthen and complicate down the list so two adjacent
 * seats are never a near-match: solid, then a plain long dash, then a dot
 * rhythm, then dash-dot. All four are legible at the hairline widths a plan
 * draws at.
 */
export const TRACE_STYLES: readonly TraceStyle[] = [
  { colorVariable: '--color-pine', label: 'solid' },
  { colorVariable: '--color-fairway', dash: '6 3', label: 'dashed' },
  { colorVariable: '--color-brass-deep', dash: '2 3', label: 'dotted' },
  { colorVariable: '--color-ink', dash: '10 3 2 3', label: 'dash-dot' },
]

/**
 * The style for a seat, by `round_players.position`.
 *
 * Positions are 1-4 and the club refuses a fifth seat, so the modulo is a
 * refusal to crash rather than a fifth tone: an out-of-range position wraps
 * back onto a tone that is already in use, which is visibly wrong on screen
 * and therefore reported, instead of throwing inside a render or inventing a
 * colour that is in no design card.
 */
export function traceStyleFor(position: number): TraceStyle {
  const seat = Number.isFinite(position) ? Math.trunc(position) : 1
  const index = ((seat - 1) % TRACE_STYLES.length + TRACE_STYLES.length) % TRACE_STYLES.length
  return TRACE_STYLES[index]!
}

/** The one place a trace tone becomes a CSS value. */
export function traceColor(position: number): string {
  return `var(${traceStyleFor(position).colorVariable})`
}

/** `stroke-dasharray` for a seat, or undefined for the solid one. */
export function traceDash(position: number): string | undefined {
  return traceStyleFor(position).dash
}

/**
 * The letter printed inside a player's strike marks — the third channel.
 *
 * `Array.from` rather than `[0]`: a display name may begin with an emoji or an
 * astral-plane character, and indexing a string would take half a surrogate
 * pair and render a replacement glyph. An empty name yields an empty string
 * rather than a placeholder letter — the club never introduces anyone by a
 * character they did not choose, and the tone and dash still tell the traces
 * apart.
 */
export function traceInitial(displayName: string): string {
  const first = Array.from(displayName.trim())[0]
  return first === undefined ? '' : first.toLocaleUpperCase()
}
